"use client";
import { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";

export function Mermaid({ chart }: { chart: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: "dark",
          securityLevel: "loose",
          htmlLabels: false,
          flowchart: { htmlLabels: false, useMaxWidth: true },
          themeVariables: {
            background: "#0b1220",
            primaryColor: "#1f2937",
            primaryBorderColor: "#6b7280",
            primaryTextColor: "#f3f4f6",
            secondaryColor: "#111827",
            tertiaryColor: "#111827",
            nodeTextColor: "#f3f4f6",
            textColor: "#f3f4f6",
            lineColor: "#9ca3af",
            // labels on edges + subgraph clusters (common invisible-text spots)
            edgeLabelBackground: "#0b1220",
            labelBackground: "#0b1220",
            clusterBkg: "#0f172a",
            clusterBorder: "#6b7280",
            titleColor: "#f3f4f6",
            fontSize: "14px",
          },
        });
        // Strip author styling directives before rendering. Mermaid bakes
        // `classDef`/`style`/`:::class` into INLINE `!important` styles on the
        // node shapes and label spans — which no external CSS can override, so a
        // chart that hardcodes e.g. a matching fill + text colour renders as
        // invisible text. Removing them lets our dark theme apply uniformly:
        // dark tiles, readable light text.
        const cleaned = chart
          .split("\n")
          .filter((line) => {
            const t = line.trim();
            if (/^classDef\s/.test(t)) return false;
            if (/^linkStyle\s/.test(t)) return false;
            if (/^style\s/.test(t)) return false;
            // flowchart `class A,B name` assignment — drop it, but keep a
            // classDiagram `class Name {` structural declaration.
            if (/^class\s/.test(t) && !t.includes("{")) return false;
            return true;
          })
          .join("\n")
          .replace(/:::[A-Za-z0-9_-]+/g, ""); // inline `node:::class` shorthand
        const id = "mmd-" + Math.random().toString(36).slice(2);
        const { svg } = await mermaid.render(id, cleaned);
        if (!cancelled && ref.current) ref.current.innerHTML = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true, html: true } });
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : "Invalid diagram");
      }
    })();
    return () => { cancelled = true; };
  }, [chart]);
  if (err) return <pre className="my-4 overflow-x-auto rounded border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300">{`Diagram error: ${err}\n\n${chart}`}</pre>;
  return <div ref={ref} className="my-4 flex justify-center overflow-x-auto" />;
}
